import { addDays } from 'date-fns';
import {
  dateKeyInTimeZone,
  dateStringToDate,
  dateToDateString,
  formatDateTimeInTimeZone,
} from '@/lib/date-values';

// Single source of truth for phone display lives in lib/contacts/phone.
export { formatPhoneDisplay as formatPhoneForDisplay } from '@/lib/contacts/phone';

type VisitAddressLike =
  | string
  | {
      street?: string | null;
      street1?: string | null;
      street2?: string | null;
      city?: string | null;
      state?: string | null;
      zip?: string | null;
      postalCode?: string | null;
      country?: string | null;
      formatted?: string | null;
    }
  | null
  | undefined;

export function normalizePhoneForTel(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

export function formatVisitAddress(address: VisitAddressLike): string | null {
  if (!address) return null;
  if (typeof address === 'string') return address.trim() || null;
  if (address.formatted?.trim()) return address.formatted.trim();

  const street = [address.street ?? address.street1, address.street2].filter(Boolean).join(', ');
  const postalCode = address.zip ?? address.postalCode;
  const tail = [address.city, address.state, postalCode].filter(Boolean).join(', ');
  return [street, tail].filter(Boolean).join(', ') || null;
}

export function formatDuration(
  startAt: string | null | undefined,
  endAt: string | null | undefined,
): string | null {
  if (!startAt || !endAt) return null;
  const minutes = Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder}m`;
  if (remainder === 0) return `${hours}h`;
  return `${hours}h ${remainder}m`;
}

export function formatHumanTimeRange(
  startAt: string | null | undefined,
  endAt: string | null | undefined,
  timezone: string | null | undefined,
  referenceNow = new Date(),
): string {
  if (!startAt) return 'Needs scheduling';

  const todayKey = dateKeyInTimeZone(referenceNow.toISOString(), timezone);
  const today = dateStringToDate(todayKey);
  const tomorrowKey = today ? dateToDateString(addDays(today, 1)) : '';
  const visitKey = dateKeyInTimeZone(startAt, timezone);
  const day =
    visitKey === todayKey
      ? 'Today'
      : visitKey === tomorrowKey
        ? 'Tomorrow'
        : formatDateTimeInTimeZone(startAt, timezone, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });

  const startTime = formatDateTimeInTimeZone(startAt, timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const endTime = endAt
    ? formatDateTimeInTimeZone(endAt, timezone, { hour: 'numeric', minute: '2-digit' })
    : '';

  if (!startTime) return day || startAt;
  return endTime ? `${day} ${startTime} - ${endTime}` : `${day} ${startTime}`;
}

/**
 * Scheduling v2 — window label that respects the visit's time-shape. An
 * ALL_DAY visit binds to a day, not a clock time (its startAt/endAt are the
 * technician's derived shift), so it reads "Mon, Jul 6 · Full Day
 * (9:00 AM – 5:00 PM shift)" instead of a misleading timed range. Every other
 * granularity falls through to formatHumanTimeRange.
 */
export function formatVisitWindow(visit: {
  startAt: string | null | undefined;
  endAt: string | null | undefined;
  timezone: string | null | undefined;
  granularity?: string | null;
  /** Day-bound route visits: the promised DAY (times come from the optimizer). */
  scheduledDate?: string | null;
}): string {
  // A pre-optimized route visit IS scheduled — to a day. 'Needs scheduling'
  // would contradict the calendar chip the user just clicked.
  if (visit.granularity === 'DAY_FLEXIBLE' && !visit.startAt && visit.scheduledDate) {
    // Local date-only render (no tz day-shift); inlined so this shared lib
    // never depends on feature-component helpers.
    const [y, m, d] = visit.scheduledDate.split('-').map(Number);
    const label = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1));
    return `${label} · All Day`;
  }
  if (visit.granularity !== 'ALL_DAY' || !visit.startAt) {
    return formatHumanTimeRange(visit.startAt, visit.endAt, visit.timezone);
  }

  const day = formatDateTimeInTimeZone(visit.startAt, visit.timezone, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const startTime = formatDateTimeInTimeZone(visit.startAt, visit.timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const endTime = visit.endAt
    ? formatDateTimeInTimeZone(visit.endAt, visit.timezone, { hour: 'numeric', minute: '2-digit' })
    : '';
  const shift = startTime && endTime ? ` (${startTime} – ${endTime} shift)` : '';
  return `${day} · Full Day${shift}`;
}
